import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Save, Eye, ChevronRight, ChevronDown, ChevronUp,
  Copy, Star, Trash2, Code, FileText, Building2, Users,
  UserCog, Wallet, Calendar
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GROUP_ICONS = {
  'Organisme': Building2, 'Session de formation': Calendar, 'Apprenant': Users,
  'Entreprise': Building2, 'Intervenant': UserCog, 'Financeur': Wallet, 'Dates': Calendar,
};

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['link'],
    [{ 'color': [] }],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'bold', 'italic', 'underline', 'strike',
  'list', 'indent', 'align', 'link', 'color',
];

const CollapsibleSection = ({ title, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left">
        <span className="text-sm font-semibold text-slate-700">{title}</span>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>
      {open && <div className="pb-4">{children}</div>}
    </div>
  );
};

const ModeleDetailPage = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEmailMode = location.pathname.includes('modeles-emails');
  const basePath = isEmailMode ? '/configuration/modeles-emails' : '/configuration/modeles-documents';

  const [template, setTemplate] = useState(null);
  const [mergeFields, setMergeFields] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});

  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [language, setLanguage] = useState('fr');

  useEffect(() => { fetchData(); }, [templateId]);

  const fetchData = async () => {
    try {
      const fetches = [
        fetch(`${API}/config/modeles/documents/${templateId}`, { credentials: 'include' }),
        fetch(`${API}/config/modeles/merge-fields`, { credentials: 'include' }),
      ];
      if (isEmailMode) {
        fetches.push(fetch(`${API}/config/modeles/emails/by-category`, { credentials: 'include' }));
      }
      const results = await Promise.all(fetches);
      const [tplRes, mfRes] = results;
      if (tplRes.ok) {
        const data = await tplRes.json();
        setTemplate(data);
        setName(data.name || '');
        setSubject(data.subject || '');
        setContent(data.content || '');
        setLanguage(data.language || 'fr');
      } else {
        toast.error("Modele non trouve");
        navigate(basePath);
      }
      if (mfRes.ok) {
        const fields = await mfRes.json();
        setMergeFields(fields);
        const exp = {};
        fields.forEach(g => { exp[g.group] = false; });
        setExpandedGroups(exp);
      }
      if (isEmailMode && results[2]?.ok) {
        setAllTemplates(await results[2].json());
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/config/modeles/documents/${templateId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, subject, content, language })
      });
      if (res.ok) toast.success("Modele sauvegarde");
      else toast.error("Erreur de sauvegarde");
    } catch (e) { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  const handlePreview = async () => {
    try {
      const res = await fetch(`${API}/config/modeles/documents/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ content })
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewHtml(data.preview);
        setShowPreview(true);
      }
    } catch (e) { toast.error("Erreur"); }
  };

  const handleInitFromTemplate = async (sourceId) => {
    if (!sourceId) return;
    try {
      const res = await fetch(`${API}/config/modeles/documents/${sourceId}`, { credentials: 'include' });
      if (res.ok) {
        const src = await res.json();
        setContent(src.content || '');
        setSubject(src.subject || '');
        toast.success("Contenu initialise depuis le modele");
      }
    } catch (e) { toast.error("Erreur"); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer ce modele ?")) return;
    try {
      await fetch(`${API}/config/modeles/documents/${templateId}`, { method: 'DELETE', credentials: 'include' });
      toast.success("Modele supprime");
      navigate(basePath);
    } catch (e) { toast.error("Erreur"); }
  };

  const handleDuplicate = async () => {
    try {
      const res = await fetch(`${API}/config/modeles/documents/${templateId}/duplicate`, {
        method: 'POST', credentials: 'include'
      });
      if (res.ok) {
        const dup = await res.json();
        toast.success("Modele duplique");
        navigate(`${basePath}/${dup.template_id}`);
      }
    } catch (e) { toast.error("Erreur"); }
  };

  const handleSetDefault = async () => {
    try {
      await fetch(`${API}/config/modeles/documents/${templateId}/default`, { method: 'PUT', credentials: 'include' });
      toast.success("Defini par defaut");
      fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  const quillModules = useMemo(() => QUILL_MODULES, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin h-8 w-8 border-4 border-violet-500 border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  // For email mode: simplified layout without left panel
  if (isEmailMode) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto space-y-6" data-testid="modele-detail-page">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <button onClick={() => navigate(basePath)} className="hover:text-slate-600">Configuration</button>
            <ChevronRight className="h-3.5 w-3.5" />
            <button onClick={() => navigate(basePath)} className="hover:text-slate-600">Modeles d'e-mails</button>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-violet-600 font-medium truncate">{name}</span>
          </div>

          <h1 className="text-2xl font-black text-slate-900" data-testid="detail-title">Modele d'e-mail</h1>

          {/* Save button at top */}
          <Button className="bg-violet-600 hover:bg-violet-700 text-white gap-2" onClick={handleSave} disabled={saving}
            data-testid="save-btn">
            <Save className="h-4 w-4" /> Enregistrer
          </Button>

          {/* Name + Language row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-slate-500">Nom du modele</Label>
              <Input value={name} onChange={e => setName(e.target.value)} className="mt-1" data-testid="template-name" />
            </div>
            <div>
              <Label className="text-sm text-slate-500">Langue</Label>
              <select value={language} onChange={e => setLanguage(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-violet-500 outline-none"
                data-testid="template-language">
                <option value="fr">Francais</option>
                <option value="en">English</option>
              </select>
              <p className="text-xs text-slate-400 mt-1">Seules les balises seront traduites dans cette langue.</p>
            </div>
          </div>

          {/* Initialize from another template */}
          <Card className="border shadow-sm">
            <CardContent className="p-4">
              <CollapsibleSection title="Initialiser avec un autre modele" defaultOpen={false}>
                <select onChange={e => handleInitFromTemplate(e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:ring-2 focus:ring-violet-500 outline-none"
                  data-testid="init-from-template">
                  <option value="">-- Choisir un modele --</option>
                  {allTemplates.filter(t => t.template_id !== templateId).map(t => (
                    <option key={t.template_id} value={t.template_id}>{t.name}</option>
                  ))}
                </select>
              </CollapsibleSection>
            </CardContent>
          </Card>

          {/* Subject */}
          <Card className="border shadow-sm">
            <CardContent className="p-4">
              <CollapsibleSection title="Objet de l'e-mail" defaultOpen={true}>
                <textarea value={subject} onChange={e => setSubject(e.target.value)}
                  className="w-full min-h-[60px] p-3 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-violet-500 outline-none resize-y"
                  placeholder="Objet de l'email..." data-testid="template-subject" />
              </CollapsibleSection>
            </CardContent>
          </Card>

          {/* Body with WYSIWYG */}
          <Card className="border shadow-sm">
            <CardContent className="p-4">
              <CollapsibleSection title="Corps de l'e-mail" defaultOpen={true}>
                <div className="email-editor" data-testid="template-content-editor">
                  <ReactQuill
                    theme="snow"
                    value={content}
                    onChange={setContent}
                    modules={quillModules}
                    formats={QUILL_FORMATS}
                    placeholder="Redigez le contenu de votre email..."
                  />
                </div>
                {/* Merge fields bar */}
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <p className="text-xs text-slate-400 mb-2 flex items-center gap-1">
                    <Code className="h-3 w-3" /> Champs de fusion disponibles
                  </p>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {mergeFields.map(group => {
                      const isExpanded = expandedGroups[group.group];
                      const GIcon = GROUP_ICONS[group.group] || FileText;
                      return (
                        <div key={group.group}>
                          <button onClick={() => setExpandedGroups(p => ({ ...p, [group.group]: !p[group.group] }))}
                            className="flex items-center gap-2 text-xs text-slate-600 hover:text-violet-600 py-1 w-full text-left">
                            <GIcon className="h-3 w-3 text-violet-400" />
                            <span className="font-medium">{group.group}</span>
                            {isExpanded ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
                          </button>
                          {isExpanded && (
                            <div className="flex flex-wrap gap-1 ml-5 mb-1">
                              {group.fields.map(f => (
                                <button key={f.tag}
                                  onClick={() => {
                                    setContent(prev => prev.replace(/<\/p>$/, '') + ' ' + f.tag + '</p>');
                                    toast.success(`${f.label} insere`);
                                  }}
                                  className="text-[10px] px-2 py-0.5 rounded bg-violet-50 text-violet-600 hover:bg-violet-100 font-mono"
                                  data-testid={`merge-field-${f.tag}`}>
                                  {f.tag.replace(/\{\{|\}\}/g, '')}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CollapsibleSection>
            </CardContent>
          </Card>

          {/* Action buttons */}
          <div className="flex items-center gap-2 pb-8">
            <Button variant="outline" size="sm" onClick={handlePreview} data-testid="preview-btn">
              <Eye className="h-4 w-4 mr-1" /> Apercu
            </Button>
            <Button variant="outline" size="sm" onClick={handleDuplicate} data-testid="duplicate-btn">
              <Copy className="h-4 w-4 mr-1" /> Dupliquer
            </Button>
            <Button variant="outline" size="sm" onClick={handleSetDefault} data-testid="default-btn">
              <Star className="h-4 w-4 mr-1" /> Par defaut
            </Button>
            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-red-500" onClick={handleDelete} data-testid="delete-btn">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Preview Modal */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="preview-modal">
            <DialogHeader>
              <DialogTitle>Apercu du modele</DialogTitle>
            </DialogHeader>
            <div className="border rounded-lg p-8 bg-white mt-4 prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: previewHtml }} data-testid="preview-content" />
          </DialogContent>
        </Dialog>
      </Layout>
    );
  }

  // Document mode: original layout with merge fields panel on left
  return (
    <Layout>
      <div className="flex gap-8" data-testid="modele-detail-page">
        {/* Left: Merge fields panel */}
        <div className="hidden lg:block w-72 shrink-0">
          <div className="sticky top-24">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 px-1 flex items-center gap-2">
              <Code className="h-3.5 w-3.5" /> Champs de fusion
            </h3>
            <p className="text-[11px] text-slate-400 mb-3 px-1">Cliquez sur un champ pour l'inserer dans le contenu</p>
            <div className="space-y-1 max-h-[calc(100vh-200px)] overflow-y-auto">
              {mergeFields.map(group => {
                const Icon = GROUP_ICONS[group.group] || FileText;
                const isExpanded = expandedGroups[group.group];
                return (
                  <div key={group.group} className="rounded-lg border border-slate-100 overflow-hidden">
                    <button onClick={() => setExpandedGroups(p => ({ ...p, [group.group]: !p[group.group] }))}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 transition-colors">
                      <span className="flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5 text-violet-500" />
                        <span className="font-medium text-slate-700">{group.group}</span>
                      </span>
                      {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-slate-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400" />}
                    </button>
                    {isExpanded && (
                      <div className="border-t border-slate-100 px-2 py-1.5 space-y-0.5 bg-slate-50/50">
                        {group.fields.map(field => (
                          <button key={field.tag}
                            onClick={() => setContent(prev => prev + field.tag)}
                            className="w-full text-left px-2 py-1.5 rounded text-xs hover:bg-violet-50 hover:text-violet-700 transition-colors group flex items-center justify-between"
                            data-testid={`merge-field-${field.tag}`}>
                            <span className="text-slate-600 group-hover:text-violet-700 truncate">{field.label}</span>
                            <code className="text-[10px] text-slate-400 group-hover:text-violet-500 ml-1 shrink-0 font-mono">
                              {field.tag.replace(/\{\{|\}\}/g, '')}
                            </code>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <button onClick={() => navigate(basePath)} className="hover:text-slate-600">Configuration</button>
            <ChevronRight className="h-3.5 w-3.5" />
            <button onClick={() => navigate(basePath)} className="hover:text-slate-600">Modeles de documents</button>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-slate-600 font-medium truncate">{name}</span>
          </div>

          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(basePath)} className="text-slate-400 hover:text-slate-600" data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <h1 className="text-2xl font-black text-slate-900 truncate" data-testid="detail-title">{name}</h1>
              {template?.is_default && <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">Par defaut</Badge>}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePreview} data-testid="preview-btn">
                <Eye className="h-4 w-4 mr-1" /> Apercu
              </Button>
              <Button variant="outline" size="sm" onClick={handleDuplicate} data-testid="duplicate-btn">
                <Copy className="h-4 w-4 mr-1" /> Dupliquer
              </Button>
              <Button variant="outline" size="sm" onClick={handleSetDefault} data-testid="default-btn">
                <Star className="h-4 w-4 mr-1" /> Par defaut
              </Button>
              <Button variant="ghost" size="icon" className="text-slate-400 hover:text-red-500" onClick={handleDelete} data-testid="delete-btn">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Name */}
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label className="text-sm font-medium text-slate-600">Nom du modele</Label>
                <Input value={name} onChange={e => setName(e.target.value)} className="mt-1" data-testid="template-name" />
              </div>
            </CardContent>
          </Card>

          {/* Content editor */}
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <Label className="text-sm font-medium text-slate-600 mb-2 block">Contenu du modele</Label>
              <textarea
                value={content}
                onChange={e => setContent(e.target.value)}
                className="w-full min-h-[450px] p-4 text-sm font-mono bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 outline-none resize-y leading-relaxed"
                placeholder="Contenu HTML du modele..."
                data-testid="template-content"
              />
            </CardContent>
          </Card>

          {/* Save */}
          <div className="flex justify-end gap-3 pb-8">
            <Button variant="outline" onClick={() => navigate(basePath)}>Annuler</Button>
            <Button className="bg-violet-600 hover:bg-violet-700 text-white px-8" onClick={handleSave} disabled={saving} data-testid="save-btn">
              <Save className="h-4 w-4 mr-2" /> Enregistrer
            </Button>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="preview-modal">
          <DialogHeader>
            <DialogTitle>Apercu du modele</DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg p-8 bg-white mt-4 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: previewHtml }} data-testid="preview-content" />
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ModeleDetailPage;
